/**
 * FMT から起こしたタスクの内容を、大分類・事業・中分類・内容に分ける。
 *
 * タスク側は分類の列を持たず、`大分類_事業_中分類_内容` を 1 つの内容に畳み込んでいる
 * （`fmt/generate-tasks.ts` の `buildTitle`。空の区分は詰める）。詳細で読むときは、
 * 長い 1 行より分けて見せたほうが「どの括りの何の作業か」がすぐ分かる。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT から起こしたタスクの内容を、大分類・事業・中分類・内容に分ける
 * @graph-connects none
 */

/** 分けた内容 */
export interface TitleParts {
  /** 大分類（`01.決算整理仕訳` など） */
  category: string;
  /** 事業（`共通` `mall` など） */
  business: string;
  /** 中分類。無ければ空 */
  middleCategory: string;
  /** 作業の名前 */
  name: string;
}

/** 大分類は `00.` 〜 `99.` の 2 桁の番号で始まる */
/** @graph-connects none */
const CATEGORY_PATTERN = /^\d{2}\./;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 内容を区分に分ける。大分類の番号で始まらない（手で作った）タスクや、区分が足りないものは分けずに null を返す。中分類が空のとき（3 区分）は、残りを内容として扱う
 * @graph-connects none
 */
export function splitTaskTitle(title: string): TitleParts | null {
  const [category, business, ...rest] = title.split('_');
  if (category === undefined || business === undefined || !CATEGORY_PATTERN.test(category)) {
    return null;
  }
  if (rest.length === 0) {
    return null;
  }
  if (rest.length === 1) {
    return { category, business, middleCategory: '', name: rest[0] ?? '' };
  }
  // 内容に `_` が含まれていても崩さないよう、中分類のあとは全部まとめて内容にする
  const [middleCategory = '', ...name] = rest;
  return { category, business, middleCategory, name: name.join('_') };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 区分と作業の名前から内容を組み立てる。大分類か事業が無ければ作業の名前だけにする（手で作ったタスクの形）。事業が欠けたまま詰めると、読み戻したときに区分の位置がずれるため。中分類は空なら詰める
 * @graph-connects none
 */
export function composeTaskTitle(parts: TitleParts): string {
  if (parts.category === '' || parts.business === '') {
    return parts.name;
  }
  return [parts.category, parts.business, parts.middleCategory, parts.name]
    .filter((part) => part !== '')
    .join('_');
}

/** 区分を選ぶときの選択肢 */
export interface TitleOptions {
  /** 大分類 */
  categories: string[];
  /** 事業 */
  businesses: string[];
  /** 大分類ごとの中分類 */
  middleByCategory: Record<string, string[]>;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business いまあるタスクの内容から、区分の選択肢（大分類・事業・大分類ごとの中分類）を集める。FMT から起こしたタスクが持つ区分をそのまま選べるようにする
 * @graph-connects splitTaskTitle [calls] 内容を区分に分ける
 */
export function listTitleOptions(titles: readonly string[]): TitleOptions {
  const categories = new Set<string>();
  const businesses = new Set<string>();
  const middles = new Map<string, Set<string>>();
  for (const title of titles) {
    const parts = splitTaskTitle(title);
    if (parts === null) {
      continue;
    }
    categories.add(parts.category);
    businesses.add(parts.business);
    if (parts.middleCategory !== '') {
      const same = middles.get(parts.category) ?? new Set<string>();
      same.add(parts.middleCategory);
      middles.set(parts.category, same);
    }
  }
  const sorted = (values: Iterable<string>) => [...values].sort((a, b) => a.localeCompare(b, 'ja'));
  return {
    categories: sorted(categories),
    businesses: sorted(businesses),
    middleByCategory: Object.fromEntries(
      [...middles.entries()].map(([category, values]) => [category, sorted(values)]),
    ),
  };
}

/** 区分を持つ種別（FMT から起こすタスクの種別） */
/** @graph-connects none */
export const KINDS_WITH_PARTS: readonly string[] = ['月次経理', '決算'];

/** 区分の選択肢の元になる FMT の項目（列のうち区分だけ） */
export interface FmtPartsSource {
  /** 大分類 */
  majorCategory: string;
  /** 事業 */
  business: string;
  /** 中分類 */
  middleCategory: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 区分を持つ種別（月次経理・決算）ごとに、区分の選択肢を集める。元は FMT の全項目（まだタスクを起こしていない区分も出す）と、その種別の既存タスク（FMT から消えた区分も選び直せる）。月次の区分と決算の区分は混ぜない
 * @graph-connects listTitleOptions [calls] 既存タスクから選択肢を集める
 */
export function listTitleOptionsByKind(
  tasks: readonly { kind: string; title: string }[],
  fmtByKind: Readonly<Record<string, readonly FmtPartsSource[]>> = {},
): Record<string, TitleOptions> {
  return Object.fromEntries(
    KINDS_WITH_PARTS.map((kind) => {
      // FMT の項目は「大分類_事業_中分類_x」の形に直して、タスクと同じ集め方に乗せる
      const fmtTitles = (fmtByKind[kind] ?? []).map((item) =>
        [item.majorCategory, item.business, item.middleCategory, 'x']
          .filter((part, index) => index === 3 || part !== '')
          .join('_'),
      );
      const taskTitles = tasks.filter((task) => task.kind === kind).map((task) => task.title);
      return [kind, listTitleOptions([...fmtTitles, ...taskTitles])];
    }),
  );
}
